import { useCallback, useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { Loader2, RefreshCw, ShieldAlert } from "lucide-react";
import { toast } from "sonner";

import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { signStoryImages } from "@/lib/story-image";

type StoryStatus = "pending" | "approved" | "published" | "rejected";
type TestimonialStatus = "pending" | "approved" | "published" | "rejected" | "archived";

type StoryRow = {
  id: string;
  full_name: string;
  email: string;
  location: string | null;
  story_title: string;
  story_body: string;
  consent_publish_story: boolean;
  consent_publish_name: boolean;
  consent_contact: boolean;
  status: StoryStatus;
  moderator_notes: string | null;
  moderated_at: string | null;
  published_at: string | null;
  image_path: string | null;
  created_at: string;
};

type TestimonialRow = {
  id: string;
  full_name: string;
  email: string;
  role_title: string | null;
  organization: string | null;
  quote: string;
  context: string | null;
  rating: number | null;
  consent_publish_quote: boolean;
  consent_publish_name: boolean;
  consent_publish_org: boolean;
  consent_use_marketing: boolean;
  release_effective_date: string | null;
  release_expires_at: string | null;
  signature_name: string | null;
  signed_at: string | null;
  status: TestimonialStatus;
  moderator_notes: string | null;
  moderated_at: string | null;
  published_at: string | null;
  created_at: string;
};

const STATUS_TONE: Record<string, string> = {
  pending: "bg-amber-500/15 text-amber-600 border-amber-500/30",
  approved: "bg-sky-500/15 text-sky-600 border-sky-500/30",
  published: "bg-emerald-500/15 text-emerald-600 border-emerald-500/30",
  rejected: "bg-destructive/15 text-destructive border-destructive/30",
  archived: "bg-muted text-muted-foreground border-border",
};

const fmt = (value: string | null) =>
  value ? new Date(value).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }) : "—";

const StatusBadge = ({ status }: { status: string }) => (
  <Badge variant="outline" className={STATUS_TONE[status] ?? ""}>
    {status}
  </Badge>
);

const ConsentChip = ({ ok, label }: { ok: boolean; label: string }) => (
  <span
    className={`rounded-full border px-2 py-0.5 text-[11px] ${
      ok ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-600" : "border-border bg-muted text-muted-foreground"
    }`}
  >
    {ok ? "✓" : "✕"} {label}
  </span>
);

type ActionKey = "approved" | "published" | "rejected" | "archived";

const ModerationCard = ({
  title,
  subtitle,
  status,
  meta,
  body,
  consents,
  notes,
  busy,
  actions,
  onAct,
}: {
  title: string;
  subtitle: string;
  status: string;
  meta: { label: string; value: string }[];
  body: React.ReactNode;
  consents: React.ReactNode;
  notes: string | null;
  busy: boolean;
  actions: ActionKey[];
  onAct: (next: ActionKey, note: string) => void;
}) => {
  const [note, setNote] = useState("");

  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between gap-4 space-y-0">
        <div>
          <CardTitle className="text-base">{title}</CardTitle>
          <p className="text-sm text-muted-foreground">{subtitle}</p>
        </div>
        <StatusBadge status={status} />
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid gap-2 text-xs text-muted-foreground sm:grid-cols-2">
          {meta.map((m) => (
            <div key={m.label}>
              <span className="font-medium text-foreground">{m.label}: </span>
              {m.value}
            </div>
          ))}
        </div>

        <div className="rounded-md border bg-muted/30 p-3 text-sm leading-relaxed">{body}</div>

        <div className="flex flex-wrap gap-1.5">{consents}</div>

        {notes ? (
          <p className="text-xs text-muted-foreground">
            <span className="font-medium text-foreground">Previous notes: </span>
            {notes}
          </p>
        ) : null}

        <div className="space-y-2">
          <Label htmlFor={`note-${title}`} className="text-xs">
            Moderator notes (saved with the decision)
          </Label>
          <Textarea
            id={`note-${title}`}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Why are you approving, publishing, or rejecting this?"
            rows={2}
          />
        </div>

        <div className="flex flex-wrap gap-2">
          {actions.includes("approved") && (
            <Button size="sm" variant="secondary" disabled={busy} onClick={() => onAct("approved", note)}>
              Approve
            </Button>
          )}
          {actions.includes("published") && (
            <Button size="sm" disabled={busy} onClick={() => onAct("published", note)}>
              Publish
            </Button>
          )}
          {actions.includes("rejected") && (
            <Button size="sm" variant="destructive" disabled={busy} onClick={() => onAct("rejected", note)}>
              Reject
            </Button>
          )}
          {actions.includes("archived") && (
            <Button size="sm" variant="outline" disabled={busy} onClick={() => onAct("archived", note)}>
              Archive
            </Button>
          )}
          {busy && <Loader2 className="h-4 w-4 animate-spin self-center text-muted-foreground" />}
        </div>
      </CardContent>
    </Card>
  );
};

const AdminSubmissions = () => {
  const { user, loading: authLoading } = useAuth();
  const [isAdmin, setIsAdmin] = useState<boolean | null>(null);
  const [stories, setStories] = useState<StoryRow[]>([]);
  const [storyImages, setStoryImages] = useState<Record<string, string>>({});
  const [testimonials, setTestimonials] = useState<TestimonialRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [showResolved, setShowResolved] = useState(false);

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      setIsAdmin(false);
      return;
    }
    supabase
      .rpc("has_role", { _user_id: user.id, _role: "admin" })
      .then(({ data, error }) => setIsAdmin(!error && data === true));
  }, [authLoading, user]);

  const load = useCallback(async () => {
    setLoading(true);
    const [s, t] = await Promise.all([
      supabase.from("story_submissions").select("*").order("created_at", { ascending: false }),
      supabase.from("testimonial_submissions").select("*").order("created_at", { ascending: false }),
    ]);
    if (s.error || t.error) {
      toast.error(s.error?.message ?? t.error?.message ?? "Failed to load submissions");
    }
    const storyRows = (s.data as StoryRow[]) ?? [];
    setStories(storyRows);
    setStoryImages(await signStoryImages(storyRows.map((r) => r.image_path ?? "")));
    setTestimonials((t.data as TestimonialRow[]) ?? []);
    setLoading(false);
  }, []);

  useEffect(() => {
    if (isAdmin) void load();
  }, [isAdmin, load]);

  const visibleStories = useMemo(
    () => stories.filter((r) => showResolved || r.status === "pending" || r.status === "approved"),
    [stories, showResolved],
  );
  const visibleTestimonials = useMemo(
    () => testimonials.filter((r) => showResolved || r.status === "pending" || r.status === "approved"),
    [testimonials, showResolved],
  );

  const pendingCount =
    stories.filter((r) => r.status === "pending").length + testimonials.filter((r) => r.status === "pending").length;

  const actStory = async (row: StoryRow, next: ActionKey, note: string) => {
    if (next === "archived") return;
    if (next === "published" && !row.consent_publish_story) {
      toast.error("Cannot publish: the submitter did not consent to publishing their story.");
      return;
    }
    setBusyId(row.id);
    const { error } = await supabase
      .from("story_submissions")
      .update({
        status: next,
        moderator_notes: note.trim() ? note.trim() : row.moderator_notes,
        moderated_by: user?.id ?? null,
        moderated_at: new Date().toISOString(),
        published_at: next === "published" ? new Date().toISOString() : null,
      })
      .eq("id", row.id);
    setBusyId(null);
    if (error) {
      toast.error(error.message);
      return;
    }
    toast.success(`Story ${next}`);
    void load();
  };

  const actTestimonial = async (row: TestimonialRow, next: ActionKey, note: string) => {
    setBusyId(row.id);
    const { error } = await supabase.rpc("set_testimonial_release_status", {
      _id: row.id,
      _status: next,
      _notes: note.trim() ? note.trim() : null,
    });
    setBusyId(null);
    if (error) {
      toast.error(error.message);
      return;
    }
    toast.success(`Testimonial ${next}`);
    void load();
  };

  if (authLoading || isAdmin === null) {
    return (
      <main className="flex min-h-screen items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </main>
    );
  }

  if (!isAdmin) {
    return (
      <main className="mx-auto flex min-h-screen max-w-md flex-col items-center justify-center gap-4 px-6 text-center">
        <ShieldAlert className="h-8 w-8 text-muted-foreground" />
        <h1 className="text-xl font-semibold">Admin access required</h1>
        <p className="text-sm text-muted-foreground">
          Sign in with an administrator account to review pending submissions.
        </p>
        <Button asChild>
          <Link to="/auth">Sign in</Link>
        </Button>
      </main>
    );
  }

  return (
    <main className="mx-auto max-w-5xl px-4 py-10">
      <Helmet>
        <title>Submission Moderation | Admin</title>
        <meta name="description" content="Internal admin view to review, approve, publish, or reject submissions." />
        <meta name="robots" content="noindex,nofollow" />
      </Helmet>

      <header className="mb-6 flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold">Submission moderation</h1>
          <p className="text-sm text-muted-foreground">
            {pendingCount} pending {pendingCount === 1 ? "item" : "items"} across stories and testimonials.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => setShowResolved((v) => !v)}>
            {showResolved ? "Hide resolved" : "Show resolved"}
          </Button>
          <Button variant="outline" size="sm" onClick={() => void load()} disabled={loading}>
            {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-2 h-4 w-4" />}
            Refresh
          </Button>
        </div>
      </header>

      <Tabs defaultValue="testimonials">
        <TabsList>
          <TabsTrigger value="testimonials">Testimonials ({visibleTestimonials.length})</TabsTrigger>
          <TabsTrigger value="stories">Stories ({visibleStories.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="testimonials" className="mt-4 space-y-4">
          {visibleTestimonials.length === 0 && (
            <p className="text-sm text-muted-foreground">Nothing to review here.</p>
          )}
          {visibleTestimonials.map((row) => (
            <ModerationCard
              key={row.id}
              title={`${row.full_name}${row.role_title ? ` — ${row.role_title}` : ""}`}
              subtitle={[row.organization, row.email].filter(Boolean).join(" · ")}
              status={row.status}
              busy={busyId === row.id}
              meta={[
                { label: "Submitted", value: fmt(row.created_at) },
                { label: "Rating", value: row.rating ? `${row.rating}/5` : "—" },
                { label: "Release effective", value: row.release_effective_date ?? "—" },
                { label: "Release expires", value: row.release_expires_at ?? "never" },
                { label: "Signature", value: row.signature_name ?? "—" },
                { label: "Signed at", value: fmt(row.signed_at) },
              ]}
              body={
                <>
                  <p>“{row.quote}”</p>
                  {row.context ? <p className="mt-2 text-xs text-muted-foreground">{row.context}</p> : null}
                </>
              }
              consents={
                <>
                  <ConsentChip ok={row.consent_publish_quote} label="quote" />
                  <ConsentChip ok={row.consent_publish_name} label="name" />
                  <ConsentChip ok={row.consent_publish_org} label="organization" />
                  <ConsentChip ok={row.consent_use_marketing} label="marketing" />
                </>
              }
              notes={row.moderator_notes}
              actions={
                row.status === "published"
                  ? ["archived", "rejected"]
                  : row.status === "approved"
                    ? ["published", "rejected", "archived"]
                    : ["approved", "published", "rejected"]
              }
              onAct={(next, note) => void actTestimonial(row, next, note)}
            />
          ))}
        </TabsContent>

        <TabsContent value="stories" className="mt-4 space-y-4">
          {visibleStories.length === 0 && <p className="text-sm text-muted-foreground">Nothing to review here.</p>}
          {visibleStories.map((row) => (
            <ModerationCard
              key={row.id}
              title={row.story_title}
              subtitle={[row.full_name, row.location, row.email].filter(Boolean).join(" · ")}
              status={row.status}
              busy={busyId === row.id}
              meta={[
                { label: "Submitted", value: fmt(row.created_at) },
                { label: "Last moderated", value: fmt(row.moderated_at) },
                { label: "Published", value: fmt(row.published_at) },
              ]}
              body={
                <>
                  <p className="whitespace-pre-wrap">{row.story_body}</p>
                  {row.image_path ? (
                    storyImages[row.image_path] ? (
                      <img
                        src={storyImages[row.image_path]}
                        alt={`Photo submitted with “${row.story_title}”`}
                        className="mt-3 max-h-64 rounded-lg border object-contain"
                      />
                    ) : (
                      <p className="mt-3 text-xs text-muted-foreground">
                        Image attached ({row.image_path}) — preview unavailable.
                      </p>
                    )
                  ) : null}
                </>
              }
              consents={
                <>
                  <ConsentChip ok={row.consent_publish_story} label="publish story" />
                  <ConsentChip ok={row.consent_publish_name} label="publish name" />
                  <ConsentChip ok={row.consent_contact} label="contact" />
                </>
              }
              notes={row.moderator_notes}
              actions={
                row.status === "published"
                  ? ["rejected"]
                  : row.status === "approved"
                    ? ["published", "rejected"]
                    : ["approved", "published", "rejected"]
              }
              onAct={(next, note) => void actStory(row, next, note)}
            />
          ))}
        </TabsContent>
      </Tabs>
    </main>
  );
};

export default AdminSubmissions;
